import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_CENTER } from "../../../services/api/api";
import { docType } from "../../../constants/DocTypeConstants";
import { IAgent } from "../listAgentSlice/listAgentSlice";
import { BANK_NAME } from "../../../utils/guards/ApplicationBankStatusGuard/types";
import { IPassportData } from "../applicationDataToBankSlice/applicationDataToBankSlice";
import { MATCH_DATA_APPLICATION_RENDER } from "../../../enums/matchDataApplicationRender";
import { IApplicationBankStatus } from "../../../interfaces/Ui/store/Application/IApplicationBankStatus";
import { TResponseApplicationData } from "../../api/application.api";

/* 
Типизация документов
*/

interface DocumentStatusProperties {
  title: string;
  color: string;
}

export const getDocumentKkbStatusProperties: (
  status?: boolean,
) => DocumentStatusProperties | undefined = (status) => {
  if (status === undefined) return;
  if (status === true) {
    return { title: "Подписано клиентом", color: "green" };
  }
  return { title: "Без подписи", color: "red" };
};

export const isDocumentsExists: (
  docs: DocumentType<DocumentKkb>[],
) => boolean = (docs) => {
  return !!docs.filter((doc) => {
    return !!doc.document;
  }).length;
};

export interface Document {
  id: number;
  application_id: number;
  bank_id: number;
  type_doc_id: number;
  name: string;
  pin?: string;
  ext: string;
  url: string;
  size: number;
  sign_file?: SignFile[];
}
export interface SignFile {
  id: number;
  kkb_document_id: number;
  name: string;
  ext: string;
  url: string;
  size: number;
}

export interface DocumentKkb extends Document {
  status_document_type: boolean;
  sign_file: SignFile[];
  member_passport_fl_id?: number;
  application_banks_id: number;
}

export interface DocumentType<T> {
  id: number;
  name: string;
  extention: string[];
  type: string;
  document: T | null;
}

export type TEditApplication = {
  app_id: number;
  initial_price: number;
  total_sum: string;
  sum_avance: number;
  start_bg_at: string;
  finish_bg_at: string;
  sum_bg: string;
  sum_bg_execution: string;
  sum_bg_obligation: string;
};

interface SelectedBankInApplication {
  id: number;
  application_id: number;
  status_id: number;
  bank_id: number;
  bank_rate?: string;
  status: IApplicationBankStatus;
  updated_at?: string;
  created_at?: string;
  documents: DocumentType<Document>[];
  platform_app_id?: null | number;
  bank: {
    id: string | number;
    title: string;
    base_rate: string;
    excess: string;
    is_enabled: boolean;
    modified_bank: boolean;
  };
  overstatement: string;
  base_commission: string;
}

/* 
  Типизация заявки в банк
  */
export interface IApplicationBank {
  overstatement: string;
  base_commission: string;
  id: number;
  application_id: number;
  status_id: number;
  bank_id: number;
  platform_app_id: null | number;
  released_at: string | null;
  is_enabled: boolean;
  status: IApplicationBankStatus;
  bank: {
    id: number;
    title: string;
    base_rate: string;
    excess: string;
    min_sum_bg: string;
    max_sum_bg: string;
    is_enabled: boolean;
    modified_bank: boolean;
  };
  documents: DocumentType<DocumentKkb>[];
}

interface IMessage {
  id: number;
  sender_user_id: number;
  receiver_user_id: number;
  application_id: number;
  text: string;
  read_at: string | null;
  is_my: boolean;
  created_at: string | null;
}

export interface IBankForAdd {
  id: number;
  title: string;
}

/* Типизация для документов, которые приходят с бэка на подписание заявки */
export interface DocumentToSignTypeDoc {
  extention: string;
  id: number;
  name: string;
  type: string;
}

export interface DocumentToSign {
  application_banks_id: number;
  ext: string;
  id: number;
  member_password_fl?: IPassportData;
  member_passowrd_fl_id: number | null;
  name: string;
  sign_file: SignFile[];
  size: number;
  type_doc: DocumentToSignTypeDoc;
  type_doc_id: number;
  url: string;
}

/* 
Типизация слайса заявки
*/
export interface IinitialStateRequestAllData {
  success: boolean;
  data: {
    application_sum_bg: string;
    id: number;
    agent_id: number;
    agent: Pick<IAgent, "id" | "name" | "surname" | "patronymic" | "type">;
    agent_bank_id: number;
    organization_id: number;
    bg_type_id: number;
    inn: string;
    total_sum: string;
    is_tax_simple: boolean;
    link: string;
    start_bg_at: string | null;
    finish_bg_at: string | null;
    documents: DocumentType<Document>[];
    organization: {
      id: number;
      inn: string;
      ogrn: string;
      name: string;
      phone_number: string;
      email: string | null;
      snils: string | null;
      created_at: string;
      updated_at: string;
      status_company: string;
    };
    purchase: {
      id: number;
      application_id: number;
      purchase_number: string;
      fz: string;
      subject_contract: string;
      is_closed: boolean;
      is_reprovision: boolean;
      is_sole_supplier: boolean;
      is_indisputable: boolean;
      purchase_total_at: string;
      purchase_end_at: string | null;
      created_at: string;
      updated_at: string;
    };
    bg_type: {
      id: number;
      name: string;
    };
    purchase_lot: {
      id: number;
      purchase_id: number | null;
      application_id: number;
      ikz: string;
      sum_bg: string;
      application_sum_bg: string;
      sum_bg_execution: string;
      sum_bg_obligation: string;
      sum_avance: string;
      total_sum: string;
      initial_price: string;
      inn_customer: number;
      name_customer: string;
      name_lot: string;
      number_lot: number;
      created_at: string;
      updated_at: string;
    } | null;
    application_banks: IApplicationBank[];
    bank: SelectedBankInApplication;
    status_id?: number;
    messages?: IMessage[];
    status: {
      id: number;
      title: string;
      color: string;
      info: string;
      info_color: string;
      type: string;
    };
    dont_attached_banks: {
      id: number;
      title: string;
      base_rate: string;
      excess: string;
      is_enabled: boolean;
    }[];
    banks: IBankForAdd[];
  };
  MATCH_DATA: MATCH_DATA_APPLICATION_RENDER;
  APPLICATION_FORM_VALUES: TEditApplication;
}
const initialState: IinitialStateRequestAllData = {
  success: false,
  data: {
    application_sum_bg: "0",
    id: 0,
    agent_id: 0,
    agent: {
      id: 0,
      name: "",
      patronymic: "",
      surname: "",
      type: "",
    },
    agent_bank_id: 0,
    organization_id: 0,
    bg_type_id: 0,
    inn: "",
    total_sum: "",
    is_tax_simple: false,
    link: "",
    start_bg_at: null,
    finish_bg_at: null,
    bg_type: { id: 0, name: "" },
    documents: [],
    organization: {
      id: 0,
      inn: "",
      ogrn: "",
      name: "",
      phone_number: "",
      email: null,
      snils: null,
      created_at: "",
      updated_at: "",
      status_company: "",
    },
    purchase: {
      id: 0,
      application_id: 0,
      purchase_number: "",
      fz: "",
      subject_contract: "",
      is_closed: false,
      is_reprovision: false,
      is_sole_supplier: false,
      is_indisputable: false,
      purchase_total_at: "",
      purchase_end_at: null,
      created_at: "",
      updated_at: "",
    },
    status: {
      id: 0,
      title: "Новая заявка",
      color: "",
      info: "",
      info_color: "",
      type: "",
    },
    purchase_lot: null,
    application_banks: [
      {
        overstatement: "",
        base_commission: "",
        id: 0,
        application_id: 0,
        status_id: 0,
        bank_id: 0,
        platform_app_id: null,
        released_at: null,
        is_enabled: false,
        status: {
          id: 0,
          title: "",
          color: "",
          info: "",
          info_color: "",
          type: "",
          bank_name: BANK_NAME.DEFAULT,
        },
        bank: {
          id: 0,
          title: "",
          base_rate: "",
          excess: "",
          min_sum_bg: "",
          max_sum_bg: "",
          is_enabled: false,
          modified_bank: false,
        },
        documents: [],
      },
    ],
    bank: {
      overstatement: "",
      base_commission: "",
      id: 0,
      application_id: 0,
      status_id: 0,
      bank_id: 0,
      status: {
        id: 0,
        title: "",
        color: "",
        info: "",
        info_color: "",
        type: "",
        bank_name: BANK_NAME.DEFAULT,
      },
      updated_at: "",
      created_at: "",
      documents: [],
      bank: {
        id: "",
        title: "",
        base_rate: "",
        excess: "",
        is_enabled: false,
        modified_bank: false,
      },
    },
    dont_attached_banks: [
      {
        id: 0,
        title: "",
        base_rate: "",
        excess: "",
        is_enabled: false,
      },
    ],
    banks: [],
  },
  MATCH_DATA: MATCH_DATA_APPLICATION_RENDER.INFO_PURCHASE,
  APPLICATION_FORM_VALUES: {
    app_id: 0,
    initial_price: 0,
    total_sum: "",
    sum_avance: 0,
    start_bg_at: "",
    finish_bg_at: "",
    sum_bg: "",
    sum_bg_execution: "",
    sum_bg_obligation: "",
  },
};

const API = new API_CENTER();

export interface IResponseEditAplicationBank {
  id: number;
  application_id: number;
  status: {
    id: number;
    title: string;
    color: string;
    info: string;
    info_color: string;
  };
  released_at: string;
  is_enabled: boolean;
  platform_app_id: number;
  bank_id: number;
}
export const API_REQUEST_EDIT_APLICATION_BANK = createAsyncThunk<
  IResponseEditAplicationBank,
  {
    bank_id: number;
    app_id: number;
    status_id: number;
    released_at: string | null;
    platform_app_id: number;
  },
  {
    rejectValue: string;
  }
>(
  "requestAppSlise/API_REQUEST_EDIT_APLICATION_BANK",
  async (value, { rejectWithValue }) => {
    const response = await API.GET_API(
      `applications/${value.app_id}/bank/${value.bank_id}`,
      "PUT",
      {
        status_id: value.status_id,
        released_at: value.released_at,
        platform_app_id: value.platform_app_id,
      },
    );
    if (response.success) {
      return response.data;
    } else {
      return rejectWithValue(response);
    }
  },
);

// данные о заявке
const requestAppSlise = createSlice({
  name: "APPLICATIONS_REQUEST_SLICES",
  initialState: initialState,
  reducers: {
    // записывает данные после создания заявки
    UP_DATA_APPLICATIONS_CREATE: (state, action) => {
      state = action.payload.data;
    },
    // записывает данные заявки при клике на неё
    SET_APPLICATION_DATA: (
      state,
      action: PayloadAction<TResponseApplicationData>,
    ) => {
      state.success = action.payload.success;

      const bank = {...state.data.bank};

      const activePayloadBankInfo = action.payload.data.application_banks.find(
        (el) => {
          return bank.bank_id === el.bank_id;
        },
      );

      if (activePayloadBankInfo) {
        state.data = { ...action.payload.data, bank: activePayloadBankInfo };
      } else {
        state.data = action.payload.data;
      }
      const { id, purchase_lot, finish_bg_at, start_bg_at, total_sum } =
        action.payload.data;

      state.APPLICATION_FORM_VALUES = {
        app_id: id,
        initial_price: purchase_lot?.initial_price
          ? Number(purchase_lot?.initial_price)
          : 0,
        total_sum: total_sum,
        sum_avance: Number(purchase_lot?.sum_avance) || 0,
        start_bg_at: start_bg_at || "",
        finish_bg_at: finish_bg_at || "",
        sum_bg: purchase_lot?.sum_bg || "",
        sum_bg_execution: purchase_lot?.sum_bg_execution || "",
        sum_bg_obligation: purchase_lot?.sum_bg_obligation || "",
      };
    },
    // переключить выбранную вкладку на заявке
    SWAP_MATCH_DATA_REQUEST: (state, action) => {
      state.MATCH_DATA = action.payload;
    },
    // переключить вкладку на выбранный банк
    SWAP_MATCH_DATA_REQUEST_BANK_ID: (
      state,
      action: PayloadAction<{
        id: number;
        value: MATCH_DATA_APPLICATION_RENDER;
      }>,
    ) => {
      const bankInfo = state.data.application_banks.find(
        (item: IApplicationBank) => item?.id === action.payload.id,
      );

      if (bankInfo) {
        state.MATCH_DATA = action.payload.value;
        state.data.bank = {...bankInfo};
        localStorage.setItem(
          "selectedApplicationBank",
          JSON.stringify(bankInfo),
        );
      }
    },

    CHANGE_EXTERNAL_ACTIVE_BANK_INFO: (
      state,
      action: PayloadAction<IApplicationBankStatus>,
    ) => {
      state.data.bank.status = action.payload;
    },

    // добавить документ к заявке
    SEARCH_UP_DATA_DOCUMENTS_ID: (
      state,
      action: PayloadAction<{ id: number | boolean; document: Document }>,
    ) => {
      if (typeof action.payload.id === "number") {
        const updatedDocuments = state.data.documents.map(
          (document: DocumentType<Document>) => {
            if (document.id === action.payload.id) {
              if (document.id !== 10) {
                return {
                  ...document,
                  document: action.payload.document,
                };
              } else {
                if (document.document?.id === +action.payload.document.id) {
                  return {
                    ...document,
                    document: action.payload.document,
                  };
                }
              }
            }
            return document;
          },
        );
        state.data.documents = updatedDocuments;
      } else if (typeof action.payload.id === "boolean") {
        const newDocuments = {
          extention: docType,
          id: 10,
          name: "Дополнительные документы",
          type: "",
          document: action.payload.document,
        };
        state.data.documents.push(newDocuments);
      }
    },
    // добавить документ к банку
    SEARCH_UP_DATA_DOCUMENTS_APLICATION_BANK_ID: (
      state,
      action: PayloadAction<{
        id: number;
        application_id: number;
        bank_id: number;
        type_doc_id: number;
        name: string;
        pin: string;
        ext: string;
        url: string;
        size: number;
      }>,
    ) => {
      state.data.application_banks.map((application: IApplicationBank) => {
        // мапимся только по документам заявки, к которой добавляем документ
        if (application.id == action.payload.id) {
          application.documents.map((document: DocumentType<Document>) => {
            if (document.id == action.payload.type_doc_id) {
              return (document.document = action.payload);
            }
          });
        }
      });
    },
    // добавить сообщение к заявке
    REQUEST_CHAT_ADD_MESSAGE: (state, action) => {
      const { messages } = state.data;
      if (messages) {
        const isDuplicate = messages.some(
          (message: IMessage) => message.id === action.payload.id,
        );

        if (!isDuplicate) {
          messages.push(action.payload);
        }
      }
    },
    // очистить данные о заявке
    CLEAR_DATA_APPLICATION: (
      state,
      action: { payload: "CLEAR" | "UPDATE" },
    ) => {
      if (action.payload === "CLEAR") {
        return (state = initialState);
      }
    },
    // архивировать банк на заявке
    ARCHIVE_APPLICATION_BANK: (state, actions) => {
      state.data.application_banks = state.data.application_banks
        .map((bank: IApplicationBank) => {
          if (
            bank.application_id === actions.payload.app_id &&
            bank.bank_id === actions.payload.bank_id
          ) {
            return {
              ...bank,
              is_enabled: actions.payload.is_enabled,
            };
          }
          return bank;
        })
        .sort((a: IApplicationBank, b: IApplicationBank) => {
          if (a.is_enabled && !b.is_enabled) {
            return -1;
          } else if (!a.is_enabled && b.is_enabled) {
            return 1;
          } else {
            return 0;
          }
        });
    },
    // изменить данные банка на заявке
    EDIT_APPLICATION_BANK_SELECTED: (state, actions) => {
      state.data.bank = { ...state.data.bank, ...actions.payload };
      localStorage.setItem(
        "selectedApplicationBank",
        JSON.stringify(state.data.bank),
      );
    },
    // изменить данные заявки при редактировании
    CHANGE_APPLICATION_FORM_VALUES: (state, actions) => {
      state.APPLICATION_FORM_VALUES = {
        ...state.APPLICATION_FORM_VALUES,
        ...actions.payload,
      };
    },
    // удаление документа
    DELETE_DOCUMENT: (state, actions) => {
      if (actions.payload) {
        if (actions.payload !== 10) {
          state.data.documents = state.data.documents.map((document) => {
            if (actions.payload === document.id) {
              return { ...document, document: null };
            }
            return document;
          });
        } else {
          state.data.documents = state.data.documents.filter(
            ({ id }) => id !== 10,
          );
        }
      }
    },
  },
});

// export const selectProfile = (state: RootState) => state.;
export const {
  UP_DATA_APPLICATIONS_CREATE,
  CLEAR_DATA_APPLICATION,
  SWAP_MATCH_DATA_REQUEST,
  SWAP_MATCH_DATA_REQUEST_BANK_ID,
  REQUEST_CHAT_ADD_MESSAGE,
  SEARCH_UP_DATA_DOCUMENTS_ID,
  SEARCH_UP_DATA_DOCUMENTS_APLICATION_BANK_ID,
  SET_APPLICATION_DATA,
  ARCHIVE_APPLICATION_BANK,
  EDIT_APPLICATION_BANK_SELECTED,
  CHANGE_APPLICATION_FORM_VALUES,
  DELETE_DOCUMENT,
  CHANGE_EXTERNAL_ACTIVE_BANK_INFO,
} = requestAppSlise.actions;
export default requestAppSlise.reducer;
